<template>
  <b-modal :active.sync="isModalActive" :width="520" scroll="keep" :can-cancel="['escape', 'outside']">
    <div class="save-success-modal rounded-lg bg-white overflow-hidden border border-[#E4E6EB] shadow-lg relative">
      <button
        @click="_OnClose"
        aria-label="Cerrar"
        class="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors duration-150">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
        </svg>
      </button>

      <div class="px-6 pt-8 pb-6 text-center">
        <!-- Icono éxito estilo SweetAlert2 -->
        <div class="save-success-modal__icon mx-auto mb-4" aria-hidden="true">
          <svg class="save-success-modal__icon-svg" viewBox="0 0 52 52">
            <circle class="save-success-modal__icon-circle" cx="26" cy="26" r="25" fill="none"/>
            <path class="save-success-modal__icon-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
          </svg>
        </div>

        <p class="text-base font-semibold text-gray-900 mb-4">
          {{ $t('save_pictogram_success_message') }}
        </p>

        <!-- Resumen centrado -->
        <ul class="inline-flex flex-col items-start mx-auto mb-5 space-y-1.5 text-sm text-gray-700 text-left">
          <li v-if="summary.dxfFileName" class="flex items-center gap-2">
            <b-icon icon="file-lines" pack="fas" size="is-small" class="text-green-600 flex-shrink-0" />
            <span>{{ $t('save_pictogram_dxf_uploaded', { filename: summary.dxfFileName }) }}</span>
          </li>
          <li v-if="summary.structures != null" class="flex items-center gap-2">
            <b-icon icon="stream" pack="fas" size="is-small" class="text-blue-600 flex-shrink-0" />
            <span>{{ summary.structures }} {{ $t('structures').toLowerCase() }}</span>
          </li>
          <li v-if="summary.pilings != null" class="flex items-center gap-2">
            <b-icon icon="band-aid" pack="fas" size="is-small" class="text-blue-600 flex-shrink-0" />
            <span>{{ summary.pilings }} {{ $t('pilings').toLowerCase() }}</span>
          </li>
          <li v-if="summary.modules != null" class="flex items-center gap-2">
            <b-icon icon="solar-panel" pack="fas" size="is-small" class="text-blue-600 flex-shrink-0" />
            <span>{{ summary.modules }} {{ $t('modules').toLowerCase() }}</span>
          </li>
          <li v-if="summary.panels != null" class="flex items-center gap-2">
            <b-icon icon="th" pack="fas" size="is-small" class="text-blue-600 flex-shrink-0" />
            <span>{{ summary.panels }} {{ $t('panels').toLowerCase() }}</span>
          </li>
          <li v-if="summary.screenshots != null" class="flex items-center gap-2">
            <b-icon icon="images" pack="fas" size="is-small" class="text-blue-600 flex-shrink-0" />
            <span>{{ summary.screenshots }} {{ $t('screenshots_uploaded') }}</span>
          </li>
        </ul>

        <p class="text-sm text-gray-600 mb-4">
          {{ $t('save_pictogram_success_question') }}
        </p>

        <!-- Acciones integradas en el contenido -->
        <div class="flex flex-nowrap items-center justify-center gap-3">
          <v-button color="info" icon="file-circle-plus" size="is-small" class="save-success-modal__action" @click="_OnNew">
            {{ $t('new_pictogram') }}
          </v-button>
          <v-button icon="arrow-left" size="is-small" class="save-success-modal__action" @click="_OnBack">
            {{ $t('back_to_list') }}
          </v-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'SaveSuccessModal',

  data() {
    return {
      isModalActive: false,
      summary: {
        dxfFileName: null,
        structures:  null,
        pilings:     null,
        modules:     null,
        panels:      null,
        screenshots: null,
      },
    }
  },

  methods: {
    /**
     * Abre el modal con el resumen del guardado.
     * @param {Object} summary - { dxfFileName, structures, pilings, modules, panels, screenshots }
     */
    show(summary = {}) {
      this.summary = {
        dxfFileName: summary.dxfFileName ?? null,
        structures:  summary.structures  ?? null,
        pilings:     summary.pilings     ?? null,
        modules:     summary.modules     ?? null,
        panels:      summary.panels      ?? null,
        screenshots: summary.screenshots ?? null,
      }
      this.isModalActive = true
    },

    hide() {
      this.isModalActive = false
    },

    _OnClose() {
      this.$emit('epc-close')
      this.hide()
    },

    _OnNew() {
      this.$emit('epc-new')
      this.hide()
    },

    _OnBack() {
      this.$emit('epc-back')
      this.hide()
    },
  },
}
</script>

<style scoped>
.save-success-modal__icon {
  width: 5rem;
  height: 5rem;
}

.save-success-modal__icon-svg {
  width: 100%;
  height: 100%;
}

.save-success-modal__icon-circle {
  stroke: #a5dc86;
  stroke-width: 2;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: save-success-stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}

.save-success-modal__icon-check {
  stroke: #a5dc86;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: save-success-stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.45s forwards;
}

.save-success-modal__action ::v-deep .button {
  font-size: 0.75rem;
  height: 2rem;
  padding-left: 0.625rem;
  padding-right: 0.625rem;
}

@keyframes save-success-stroke {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
